import { useEffect, useRef, useState, useCallback } from 'react';
import { API_BASE_URL } from '../services/api';
import { FlashDir } from './useLivePrices';

// ──────────────────────────────────────────────────────────────────────────
// useLiveMarket — SSE stream of dashboard market data (indexes, ETFs,
// commodities). Mirrors useLivePrices but for the MarketDataCache.
//
// Relays the existing server-side cache only — zero extra yfinance load.
// Emits per-symbol flash signals (up/down) when a price changes so the
// dashboard tables can flash cells like an exchange terminal.
// ──────────────────────────────────────────────────────────────────────────

export interface MarketItem {
  symbol: string;
  name: string;
  price: number;
  chg_1d: number;
  chg_1m?: number | null;
  chg_ytd?: number | null;
  chg_1y?: number | null;
  category?: string;
  [key: string]: any;
}

interface UseLiveMarketResult {
  indexes: MarketItem[];
  etfs: MarketItem[];
  commodities: MarketItem[];
  lastUpdated: string | null;
  connected: boolean;
  /** symbol -> flash direction (auto-clears ~850ms after change) */
  flashes: Record<string, FlashDir>;
}

const FLASH_MS = 5500;  // keep the colour ~5s post-change (matches CSS animation)
const EMPTY: MarketItem[] = [];

export function useLiveMarket(enabled: boolean = true): UseLiveMarketResult {
  const [indexes, setIndexes] = useState<MarketItem[]>(EMPTY);
  const [etfs, setEtfs] = useState<MarketItem[]>(EMPTY);
  const [commodities, setCommodities] = useState<MarketItem[]>(EMPTY);
  const [lastUpdated, setLastUpdated] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);
  const [flashes, setFlashes] = useState<Record<string, FlashDir>>({});

  // One merged map per category so we can apply partial deltas
  const maps = useRef<{ [k: string]: Map<string, MarketItem> }>({
    indexes: new Map(), etfs: new Map(), commodities: new Map(),
  });
  const flashTimers = useRef<Record<string, ReturnType<typeof setTimeout>>>({});

  const clearFlash = useCallback((sym: string) => {
    setFlashes(f => {
      if (!(sym in f)) return f;
      const next = { ...f }; delete next[sym]; return next;
    });
  }, []);

  useEffect(() => {
    if (!enabled) return;
    const _tok = localStorage.getItem("auth_token");
    const es = new EventSource(`${API_BASE_URL}/api/stream/market${_tok ? `?token=${_tok}` : ""}`);
    es.onopen = () => setConnected(true);
    es.onerror = () => setConnected(false);

    const setterFor = (cat: string) =>
      cat === 'indexes' ? setIndexes : cat === 'etfs' ? setEtfs : setCommodities;

    const onSnapshot = (e: MessageEvent) => {
      try {
        const snap = JSON.parse(e.data);
        (['indexes', 'etfs', 'commodities'] as const).forEach(cat => {
          const m = new Map<string, MarketItem>();
          for (const it of snap[cat] || []) m.set(it.symbol, it);
          maps.current[cat] = m;
          setterFor(cat)(snap[cat] || []);
        });
        setLastUpdated(snap.last_updated ?? null);
      } catch { /* ignore */ }
    };

    const onUpdate = (e: MessageEvent) => {
      try {
        const upd = JSON.parse(e.data);
        const newFlashes: Record<string, FlashDir> = {};
        (['indexes', 'etfs', 'commodities'] as const).forEach(cat => {
          const changed = upd[cat] || [];
          if (!changed.length) return;
          const m = maps.current[cat];
          for (const it of changed) {
            const prev = m.get(it.symbol);
            if (prev && prev.price != null && it.price != null && it.price !== prev.price) {
              newFlashes[it.symbol] = it.price > prev.price ? 'up' : 'down';
            }
            m.set(it.symbol, it);
          }
          setterFor(cat)(Array.from(m.values()));
        });
        setLastUpdated(upd.last_updated ?? null);

        const syms = Object.keys(newFlashes);
        if (syms.length) {
          setFlashes(f => ({ ...f, ...newFlashes }));
          for (const sym of syms) {
            if (flashTimers.current[sym]) clearTimeout(flashTimers.current[sym]);
            flashTimers.current[sym] = setTimeout(() => clearFlash(sym), FLASH_MS);
          }
        }
      } catch { /* ignore */ }
    };

    es.addEventListener('snapshot', onSnapshot as EventListener);
    es.addEventListener('update', onUpdate as EventListener);

    return () => {
      es.removeEventListener('snapshot', onSnapshot as EventListener);
      es.removeEventListener('update', onUpdate as EventListener);
      es.close();
      Object.values(flashTimers.current).forEach(clearTimeout);
      flashTimers.current = {};
    };
  }, [enabled, clearFlash]);

  return { indexes, etfs, commodities, lastUpdated, connected, flashes };
}
